import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Quill from 'quill';
import 'quill/dist/quill.bubble.css';
import { truncateHtmlContent } from '../utils/html-truncate.util';

interface WysiwygViewerProps {
  value?: string;
  'data-testid'?: string;
  maxLines?: number;
  showExpandButton?: boolean;
}

export const WysiwygViewer: React.FC<WysiwygViewerProps> = ({
  value = '',
  'data-testid': testId,
  maxLines = 2,
  showExpandButton = true,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [truncatedContent, setTruncatedContent] = useState('');
  const [isTruncated, setIsTruncated] = useState(false);

  // Initialize truncated content
  useEffect(() => {
    if (!value || !showExpandButton) {
      setTruncatedContent(value);
      setIsTruncated(false);
      return;
    }

    const { truncatedHtml, isTruncated: contentIsTruncated } = truncateHtmlContent(value, maxLines);
    setTruncatedContent(truncatedHtml);
    setIsTruncated(contentIsTruncated);
  }, [value, maxLines, showExpandButton]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Clean up existing Quill instance
    if (quillRef.current) {
      quillRef.current = null;
    }

    // Initialize Quill with bubble theme for read-only viewing
    quillRef.current = new Quill(containerRef.current, {
      theme: 'bubble',
      readOnly: true,
      modules: {
        toolbar: false, // No toolbar for viewer
      },
    });

    // Set content based on expansion state
    const contentToShow = isExpanded ? value : truncatedContent;
    if (contentToShow) {
      quillRef.current.setContents(quillRef.current.clipboard.convert({html: contentToShow}), 'silent');
    }

    return () => {
      if (quillRef.current) {
        quillRef.current = null;
      }
    };
  }, [value, truncatedContent, isExpanded]);

  // If no content, show placeholder
  if (!value || value.trim() === '' || value === '<p><br></p>') {
    return (
      <Typography variant="body2" color="text.secondary">
        No description - please consider adding one!
      </Typography>
    );
  }

  return (
    <Box data-testid={testId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box
        sx={{
          flex: 1,
          '& .ql-editor': {
            padding: '0 !important',
            fontSize: '0.875rem !important', // Match Typography body2
            lineHeight: '1.43 !important',
            color: theme.palette.text.primary + ' !important',
          },
          '& .ql-editor p': {
            margin: '0 0 8px 0',
            '&:last-child': {
              marginBottom: 0,
            },
          },
          '& .ql-editor strong': { fontWeight: 600 },
          '& .ql-editor em': { fontStyle: 'italic' },
          '& .ql-editor ul, & .ql-editor ol': {
            paddingLeft: '20px',
            margin: '8px 0'
          },
          // Fix double margins when lists follow paragraphs - use !important for bubble theme
          '& .ql-editor p + ul, & .ql-editor p + ol': {
            marginTop: '0 !important',
          },
          // Additional bubble theme specific selectors
          '& p + ul, & p + ol': {
            marginTop: '0 !important',
          },
          '& .ql-editor li': { marginBottom: '4px' },
          '& .ql-container': {
            border: 'none !important',
          }
        }}
      >
        <div ref={containerRef} />
      </Box>
      {showExpandButton && isTruncated && (
        <IconButton
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            color: theme.palette.primary.main,
            padding: '2px',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
          aria-label={isExpanded ? 'Show less content' : 'Show more content'}
        >
          {isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
        </IconButton>
      )}
    </Box>
  );
};

export default WysiwygViewer;